import React from 'react';
import { X } from 'lucide-react';

const TermsConditions = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-orange-500 text-white p-6 flex items-center justify-between rounded-t-3xl">
          <h2 className="text-3xl font-black">Terms & Conditions</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-8 space-y-6 text-gray-800">
          <p className="text-sm text-gray-600">
            <strong>Last Updated:</strong> October 23, 2025
          </p>
          
          <section>
            <h3 className="text-2xl font-bold text-orange-600 mb-3">1. Acceptance of Terms</h3>
            <p className="leading-relaxed">
              By accessing and using this website, you accept and agree to be bound by the terms and conditions of this agreement. If you do not agree to these terms, please do not use this website.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-orange-600 mb-3">2. About Hope Cathedral</h3>
            <p className="leading-relaxed">
              Hope Cathedral is a comedy TV series in development by Dunamis - On-Stage Productions in partnership with Hekspace Studios, Kherut Films, and Reel, Flicks & Art. This website serves as an informational and fundraising platform for the series.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-orange-600 mb-3">3. Intellectual Property Rights</h3>
            <p className="leading-relaxed mb-3">
              All content on this website, including but not limited to text, graphics, logos, images, videos, and software, is the property of Hope Cathedral/Dunamis - On-Stage Productions or its content suppliers and is protected by international copyright laws.
            </p>
            <p className="leading-relaxed">
              The Hope Cathedral concept, characters, storylines, and all related creative materials are the exclusive intellectual property of Dunamis - On-Stage Productions and its partners. Unauthorized use, reproduction, or distribution is strictly prohibited.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-orange-600 mb-3">4. Pledges and Financial Support</h3>
            <p className="leading-relaxed mb-3">
              When you submit a pledge through our website:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Pledges are expressions of intent to support the Hope Cathedral production</li>
              <li>Payment processing will be configured and communicated separately</li>
              <li>Pledge amounts and tiers are subject to the terms described on the support page</li>
              <li>We reserve the right to accept or decline any pledge</li>
              <li>Pledges are non-refundable once payment is processed, except as required by law</li>
              <li>Perks and rewards associated with pledge tiers will be fulfilled based on production timeline and availability</li>
            </ul>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-orange-600 mb-3">5. Production Timeline</h3>
            <p className="leading-relaxed">
              The first screening is scheduled for November 2025. However, production timelines are subject to change due to various factors including funding, casting, location availability, and unforeseen circumstances. We will make reasonable efforts to keep supporters informed of any significant changes.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-orange-600 mb-3">6. User Submissions</h3>
            <p className="leading-relaxed">
              By submitting information through our forms (pledge forms, contact forms, newsletter signup), you grant us permission to use this information as described in our Privacy Policy. You warrant that all information you provide is accurate and complete.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-orange-600 mb-3">7. Third-Party Links and Services</h3>
            <p className="leading-relaxed">
              Our website may contain links to third-party websites or services (such as social media platforms). We are not responsible for the content, privacy policies, or practices of these third-party sites. We use Web3Forms for form processing, which has its own terms and privacy policy.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-orange-600 mb-3">8. Disclaimer of Warranties</h3>
            <p className="leading-relaxed">
              This website and all information, content, materials, and services included on or otherwise made available to you through this website are provided on an "as is" and "as available" basis. We make no warranties or representations about the accuracy or completeness of the website's content.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-orange-600 mb-3">9. Limitation of Liability</h3>
            <p className="leading-relaxed">
              To the fullest extent permitted by law, Hope Cathedral/Dunamis - On-Stage Productions and its partners shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the website or services.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-orange-600 mb-3">10. Content Accuracy</h3>
            <p className="leading-relaxed">
              While we strive to provide accurate information about the Hope Cathedral series, cast, crew, and production details, information is subject to change. The proposed cast list represents our creative vision and is subject to final casting decisions and availability.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-orange-600 mb-3">11. Prohibited Uses</h3>
            <p className="leading-relaxed mb-3">You may not use this website:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>For any unlawful purpose or to solicit others to perform unlawful acts</li>
              <li>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
              <li>To infringe upon or violate our intellectual property rights or the rights of others</li>
              <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
              <li>To submit false or misleading information</li>
              <li>To upload or transmit viruses or any other type of malicious code</li>
            </ul>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-orange-600 mb-3">12. Governing Law</h3>
            <p className="leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-orange-600 mb-3">13. Changes to Terms</h3>
            <p className="leading-relaxed">
              We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. Continued use of the website after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-orange-600 mb-3">14. Contact Information</h3>
            <p className="leading-relaxed">
              If you have any questions about these Terms & Conditions, please contact us:
            </p>
            <div className="mt-3 p-4 bg-orange-50 rounded-xl">
              <p><strong>Email:</strong> <a href="mailto:hopecathedralshow@gmail.com" className="text-teal-600 hover:text-teal-700">hopecathedralshow@gmail.com</a></p>
              <p className="mt-2"><strong>Production Company:</strong> Dunamis - On-Stage Productions</p>
              <p className="mt-2"><strong>Partners:</strong> Hekspace Studios · Kherut Films · Reel, Flicks & Art</p>
            </div>
          </section>

          <section className="bg-pink-50 p-6 rounded-xl">
            <h3 className="text-xl font-bold text-pink-600 mb-3">Important Notice</h3>
            <p className="leading-relaxed text-sm">
              By supporting Hope Cathedral, you acknowledge that you are contributing to a creative project in development. While we are committed to delivering a high-quality production, the entertainment industry involves inherent uncertainties. We will make every reasonable effort to fulfill our commitments to supporters and keep you informed throughout the production process.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
